import {
    ElectionDefinition,
    LoadedElectionDefinition,
} from '../election/electionTypes'
import { UseQueryOptions } from '@tanstack/react-query'
import { getElectionData } from './get-election-data'
import { consoleLogger } from '@west-australian-newspapers/typescript-log'

type BuildElectionDefinitionQueryArgs = {
    initialDefinition: ElectionDefinition
    electionAPI: string
    caller: string
}

type ElectionDefinitionQueryKey = ['election-api', string, 'definition']

type ElectionDefinitionQueryOptions<TData> = UseQueryOptions<
    LoadedElectionDefinition,
    unknown,
    TData,
    ElectionDefinitionQueryKey
>

// Overloaded rather than one generic signature: without `select`, a generic
// TData gets inferred from useQuery's parameter instead of defaulting, which
// breaks the narrowing of `data` for every existing caller.
export function buildElectionDefinitionQuery(
    args: BuildElectionDefinitionQueryArgs,
): ElectionDefinitionQueryOptions<LoadedElectionDefinition>
export function buildElectionDefinitionQuery<TData>(
    args: BuildElectionDefinitionQueryArgs & {
        /** Derives what the component needs from the shared, cached definition. */
        select: (definition: LoadedElectionDefinition) => TData
    },
): ElectionDefinitionQueryOptions<TData>
export function buildElectionDefinitionQuery<TData>({
    initialDefinition,
    electionAPI,
    caller,
    select,
}: BuildElectionDefinitionQueryArgs & {
    select?: (definition: LoadedElectionDefinition) => TData
}): ElectionDefinitionQueryOptions<TData> {
    const dataFrequency = initialDefinition.electionData
        ? (initialDefinition.electionData.config?.electionApi.frequency ?? 1) *
          60 *
          1000
        : 60 * 1000

    return {
        queryKey: ['election-api', initialDefinition.electionId, 'definition'],
        queryFn: async (): Promise<LoadedElectionDefinition> => {
            const electionData = await getElectionData(
                consoleLogger(),
                electionAPI,
                caller,
                initialDefinition.electionId,
            )
            return {
                ...initialDefinition,
                electionData,
            }
        },
        // Only provide initial data if it's loaded.
        ...(initialDefinition.electionData
            ? {
                  initialData: initialDefinition,
              }
            : {}),
        // Do not refetch on initial render. Wait a single dataFrequency interval.
        staleTime: dataFrequency,
        // Refetch every dataFrequency interval.
        refetchInterval: dataFrequency,
        refetchOnWindowFocus: 'always',
        select,
    }
}
